import { supabaseClient } from '../supabase';
import { queryPolicies } from '../queryPolicies';

export function merchantDetailRequest(merchantId: number, schema: string) {
  return {
    tableName: 'merchant',
    schema,
    deep: true,
    conditions: {
      id: {
        filterType: 'number' as const,
        type: 'equals' as const,
        filter: merchantId,
        colId: 'id',
      },
    },
    limitOffset: { limit: 1, offset: 0 },
    withCount: false,
    singleRow: true,
  };
}

export function merchantSubmissionsRequest(merchantId: number, schema: string) {
  return {
    tableName: 'submission',
    schema,
    deep: true,
    conditions: {
      merchant: {
        filterType: 'number' as const,
        type: 'equals' as const,
        filter: merchantId,
        colId: 'merchant',
      },
    },
    orderBy: { column: 'updated_at', direction: 'descending' as const },
  };
}

export interface ActivePrimaryOverride {
  id: number;
  merchant: number;
  submission: number;
  submission_label: string | null;
  reason: string | null;
  creator_name: string | null;
  created_at: string;
}

export function activePrimaryOverrideQueryOptions(merchantId: number) {
  return {
    queryKey: ['primary-approval-override', merchantId] as const,
    queryFn: async (): Promise<ActivePrimaryOverride | null> => {
      const { data, error } = await (supabaseClient.rpc as any)('get_active_primary_approval_override', {
        p_merchant: merchantId,
        p_submission: null,
      });
      if (error) throw error;
      return (data?.[0] as ActivePrimaryOverride | undefined) ?? null;
    },
    staleTime: queryPolicies.recordDetail.staleTime,
    gcTime: queryPolicies.recordDetail.gcTime,
    refetchOnReconnect: true,
  };
}

interface DealCycleSummary {
  id: number;
  cycle_sequence: number;
  close_reason: string | null;
  closed_at: string | null;
}

export interface MerchantDealCycles {
  cycleBySubmission: Map<number, number | null>;
  cycleById: Map<number, DealCycleSummary>;
}

export function merchantDealCyclesQueryOptions(merchantId: number) {
  return {
    queryKey: ['merchant-deal-cycles', merchantId] as const,
    staleTime: 60_000,
    queryFn: async (): Promise<MerchantDealCycles> => {
      const supabase = supabaseClient as any;
      const [links, cycles] = await Promise.all([
        supabase.from('submission').select('id,deal_cycle').eq('merchant', merchantId),
        supabase.from('deal_cycle').select('id,cycle_sequence,close_reason,closed_at').eq('merchant', merchantId),
      ]);
      if (links.error) throw links.error;
      if (cycles.error) throw cycles.error;
      return {
        cycleBySubmission: new Map<number, number | null>(
          (links.data ?? []).map((row: any) => [row.id, row.deal_cycle ?? null]),
        ),
        cycleById: new Map<number, DealCycleSummary>(
          (cycles.data ?? []).map((row: any) => [row.id, row]),
        ),
      };
    },
  };
}
